// Shared market type configuration used across EV / ARB / MIDDLES / BONUS pages.
//
// Four mappings, each used for a different surface:
//
//  1. MARKET_TYPE_FILTER_MAPPING — display name → array of raw API codes.
//     Used by the filter dropdown to group regular + milestone variants under
//     one checkbox.
//
//  2. MARKET_TYPE_HIERARCHY — UI tree for the filter dropdown.
//     Top-level categories → sub-categories → display names.
//
//  3. MARKET_TYPE_MAPPING — raw API code → display name.
//     Used to render the "Market Type" column in each opportunity row.
//     Milestone variants get their own distinct display name here so users
//     can tell at a glance which variant a row is.
//
//  4. SELECTION_SHORT_NAMES — raw API code → short label.
//     Used inside the "Selection" cell to abbreviate market names when they
//     appear inside a longer sentence (e.g. "LeBron Over 35.5 PRA").
//     Markets without an entry here fall through to other formatting logic.
//
//  5. ALL_MARKET_TYPES — derived from FILTER_MAPPING for "select all" / default
//     filter state.

export const MARKET_TYPE_FILTER_MAPPING = {
  // NBA/Basketball Player Props (Regular + Milestones combined)
  "Player Points": ["player_points", "player_points_milestones"],          // shared with NHL
  "Player Rebounds": ["player_rebounds", "player_rebounds_milestones"],
  "Player Assists": ["player_assists", "player_assists_milestones"],       // shared with NHL
  "Player Three Pointers Made": ["player_made_threes", "player_made_threes_milestones"],
  "Player Steals": ["player_steals", "player_steals_milestones"],
  "Player Blocks": ["player_blocks", "player_blocks_milestones"],
  "Points + Rebounds + Assists": ["player_points_rebounds_assists", "player_points_rebounds_assists_milestones"],
  "Points + Rebounds": ["player_points_rebounds", "player_points_rebounds_milestones"],
  "Points + Assists": ["player_points_assists", "player_points_assists_milestones"],
  "Rebounds + Assists": ["player_rebounds_assists", "player_rebounds_assists_milestones"],

  // MLB Batter Props (Regular + Milestones combined)
  "Batter Home Runs": ["batter_home_runs", "batter_home_runs_milestones"],
  "Batter Hits": ["batter_hits", "batter_hits_milestones"],
  "Batter RBIs": ["batter_rbis", "batter_rbis_milestones"],
  "Batter Runs": ["batter_runs", "batter_runs_milestones"],
  "Batter Singles": ["batter_singles", "batter_singles_milestones"],
  "Batter Total Bases": ["batter_total_bases", "batter_total_bases_milestones"],
  "Batter Walks": ["batter_walks", "batter_walks_milestones"],
  "Batter Strikeouts": ["batter_strikeouts", "batter_strikeouts_milestones"],
  "Batter Hits + Runs + RBIs": ["batter_hits_runs_rbis", "batter_hits_runs_rbis_milestones"],

  // MLB Pitcher Props (Regular + Milestones combined)
  "Pitcher Strikeouts": ["pitcher_strikeouts", "pitcher_strikeouts_milestones"],
  "Pitcher Earned Runs": ["pitcher_earned_runs", "pitcher_earned_runs_milestones"],
  "Pitcher Hits Allowed": ["pitcher_hits_allowed", "pitcher_hits_allowed_milestones"],
  "Pitcher Walks Allowed": ["pitcher_walks_allowed", "pitcher_walks_allowed_milestones"],
  "Pitcher Outs Recorded": ["pitcher_outs", "pitcher_outs_milestones"],

  // NFL Passing Props (Regular + Milestones combined)
  "Passing Yards": ["player_passing_yds", "player_passing_yds_milestones"],
  "Passing Touchdowns": ["player_passing_tds", "player_passing_tds_milestones"],
  "Longest Pass": ["player_longest_pass", "player_longest_pass_milestones"],
  "Passing Interceptions": ["player_passing_interceptions", "player_passing_interceptions_milestones"],
  "Passing + Rushing Yards": ["player_passing_rushing_yds", "player_passing_rushing_yds_milestones"],
  "Passing Completions": ["player_passing_completions", "player_passing_completions_milestones"],
  "Passing Attempts": ["player_passing_attempts", "player_passing_attempts_milestones"],

  // NFL Rushing Props (Regular + Milestones combined)
  "Rushing Yards": ["player_rushing_yds", "player_rushing_yds_milestones"],
  "Rushing Attempts": ["player_rushing_attempts", "player_rushing_attempts_milestones"],
  "Longest Rush": ["player_longest_rush", "player_longest_rush_milestones"],
  "Rushing + Receiving Yards": ["player_rushing_receiving_yds", "player_rushing_receiving_yds_milestones"],

  // NFL Receiving Props (Regular + Milestones combined)
  "Receiving Yards": ["player_receiving_yds", "player_receiving_yds_milestones"],
  "Receptions": ["player_receptions", "player_receptions_milestones"],
  "Longest Reception": ["player_longest_reception", "player_longest_reception_milestones"],

  // NFL Defensive Props. Tackles + Assists is COMBINED tackles (one stat, the
  // way the books settle it) and has no milestone twin in the API.
  "Tackles + Assists": ["player_tackles_assists", "player_tackles_assists_milestones"],
  "Sacks": ["player_sacks", "player_sacks_milestones"],

  // NFL Kicking Props. Kicking Points counts field goals AND extra points, so
  // it is a different stat from Field Goals Made, not a rollup of it.
  "Kicking Points": ["player_kicking_points", "player_kicking_points_milestones"],
  "Field Goals Made": ["player_field_goals_made", "player_field_goals_made_milestones"],

  // NFL Touchdown Scorers. Anytime / 2+ / 3+ collapse into the ONE milestones
  // key separated by point (anytime IS Over @ 0.5), so there is no O/U twin to
  // pair with here - the same shape as "Player Tries" for NRL below.
  "Touchdown Scorer": ["player_touchdowns_milestones"],
  "First Touchdown Scorer": ["player_1st_touchdown_scorer"],

  // NHL Player Props (Regular + Milestones combined)
  // Note: "Player Points" and "Player Assists" are shared with NBA above.
  "Player Goals": ["player_goals", "player_goals_milestones"],
  "Player Shots on Goal": ["player_shots_on_goal", "player_shots_on_goal_milestones"],

  // AFL Player Props
  // Asymmetric: only Disposals has a regular variant; the rest are milestone-only.
  "Player Disposals": ["player_disposals", "player_disposals_milestones"],
  "Player Fantasy Points": ["player_fantasy_points_milestones"],
  "Player Marks": ["player_marks_milestones"],
  "Player Tackles": ["player_tackles_milestones"],
  "Player Kicks": ["player_kicks_milestones"],
  "Player Handballs": ["player_handballs_milestones"],
  "Player Clearances": ["player_clearances_milestones"],

  // NRL Player Props (milestone-only)
  // Note: "Player Points" (player_points_milestones) is shared with NBA/NHL above.
  "Player Tries": ["player_tries_milestones"],
  "First Tryscorer": ["player_1st_tryscorer"],
  "First or Second Tryscorer": ["player_1st_or_2nd_tryscorer"],
  "First, Second or Third Tryscorer": ["player_1st_2nd_or_3rd_tryscorer"],

  // Soccer Player Props (Regular + Milestones combined).
  // "Player Goals" and "Player Assists" are NOT here — those display names are
  // already taken by the NHL/NBA sections above and the raw codes are shared,
  // so soccer reuses them. Only soccer-specific stats get new entries.
  "Player Shots": ["player_shots", "player_shots_milestones"],
  "Player Shots on Target": ["player_shots_on_target", "player_shots_on_target_milestones"],
  "Player Tackles (Soccer)": ["player_tackles", "player_tackles_milestones"],
  "Player Fouls Committed": ["player_fouls_committed", "player_fouls_committed_milestones"],
  "Player Cards": ["player_cards", "player_cards_milestones"],
  "Player Red Cards": ["player_red_cards", "player_red_cards_milestones"],
  "Goalkeeper Saves": ["goalkeeper_saves", "goalkeeper_saves_milestones"],
  "First Goalscorer": ["player_1st_goalscorer"],

  // Tennis (Mens Slams) Player Props. player_total_aces groups its O/U + milestone
  // variants under one checkbox (like the NBA props above). player_sets_won is the
  // "to win at least one set" market normalised to O/U 0.5.
  "Player Games Won": ["player_games_won"],
  "Player Total Aces": ["player_total_aces", "player_total_aces_milestones"],
  "Player Sets Won": ["player_sets_won"],

  // Game Markets - Head to Head
  "Head To Head": ["head_to_head"],
  "Draw No Bet": ["draw_no_bet"],
  "Both Teams To Score": ["both_teams_to_score"],
  // MMA (UFC). Yes/No, priced like Both Teams To Score.
  "Fight Goes The Distance": ["fight_goes_distance"],
  "Head To Head - 3 Way": ["head_to_head_3_way"],
  "Head To Head - 3 Way 1st Half": ["head_to_head_3_way_1st_half"],
  "Draw No Bet - 1st Half": ["draw_no_bet_1st_half"],
  "Both Teams To Score - 1st Half": ["both_teams_to_score_1st_half"],
  "Head To Head - 1st 5 Innings": ["head_to_head_1st_5_innings"],
  "Head To Head - 1st Half": ["head_to_head_1st_half"],
  "Head To Head - 1st Period": ["head_to_head_1st_period"],
  "Head To Head - 2nd Period": ["head_to_head_2nd_period"],
  "Head To Head - 3rd Period": ["head_to_head_3rd_period"],
  // Tennis (Mens Slams) per-set winner
  "Head To Head - 1st Set": ["head_to_head_1st_set"],
  "Head To Head - 2nd Set": ["head_to_head_2nd_set"],
  // Soccer 2nd-half result markets
  "Head To Head - 3 Way 2nd Half": ["head_to_head_3_way_2nd_half"],
  "Draw No Bet - 2nd Half": ["draw_no_bet_2nd_half"],
  "Both Teams To Score - 2nd Half": ["both_teams_to_score_2nd_half"],
  // Soccer correct score (full-time scoreline)
  "Correct Score": ["correct_score"],
  "Correct Score - 1st Half": ["correct_score_1st_half"],
  "Correct Score - 2nd Half": ["correct_score_2nd_half"],

  // Total Markets
  "Total Points": ["alternate_total_points"],
  "Total Goals": ["alternate_total_goals"],
  "Total Tries": ["alternate_total_tries"],
  "Total Rounds": ["alternate_total_rounds"],
  "Total Corners": ["alternate_total_corners"],
  "Total Cards": ["alternate_total_cards"],
  "Total Runs": ["alternate_total_runs"],
  "Total Runs - 1st 5 Innings": ["alternate_total_runs_1st_5_innings"],
  "Total Runs - 1st Inning": ["alternate_total_runs_1st_1_innings"],
  // Winning-margin bands (ODDS SCREEN ONLY — see ODDS_ONLY_MARKETS in
  // ODDS_PAGE/oddsTaxonomy.js). One entry PER CUT: the books price several cuts
  // on the same game and they are different bets, so they must never merge.
  // Deliberately absent from MARKET_TYPE_HIERARCHY, which the EV page renders
  // directly as its market filter — the bots do not produce these.
  "Winning Margin 1-39 / 40+": ["winning_margin_39"],
  "Winning Margin 1-24 / 25+": ["winning_margin_24"],
  "Winning Margin 1-12 / 13+": ["winning_margin_12"],
  // MMA (UFC) odds-screen markets - shown via ODDS_ONLY_MARKETS, not the bot
  // filter tree, since the bots only price H2H / Goes The Distance / Total Rounds.
  // Method of Victory and Round Betting repeat the fighter name and separate
  // outcomes by `description`, the same shape as the winning-margin bands.
  "Method of Victory": ["method_of_victory"],
  "Round Betting": ["round_betting"],
  "How Fight Ends": ["how_fight_ends"],
  "Fight To Start Round": ["fight_to_start_round"],
  "Fighter Significant Strikes": ["player_significant_strikes_milestones"],
  "Fighter Total Strikes": ["player_strikes_milestones"],
  "Fighter Takedowns": ["player_takedowns_milestones"],
  "Total Touchdowns": ["alternate_total_touchdowns"],
  "Total Points - 1st Half": ["alternate_total_points_1st_half"],
  "Total Tries - 1st Half": ["alternate_total_tries_1st_half"],
  "Total Goals - 1st Half": ["alternate_total_goals_1st_half"],
  "Total Corners - 1st Half": ["alternate_total_corners_1st_half"],
  "Total Cards - 1st Half": ["alternate_total_cards_1st_half"],
  "Total Goals - 1st Period": ["alternate_total_goals_1st_period"],
  "Total Goals - 2nd Period": ["alternate_total_goals_2nd_period"],
  "Total Goals - 3rd Period": ["alternate_total_goals_3rd_period"],
  // Soccer 2nd-half totals
  "Total Goals - 2nd Half": ["alternate_total_goals_2nd_half"],
  "Total Corners - 2nd Half": ["alternate_total_corners_2nd_half"],
  "Total Cards - 2nd Half": ["alternate_total_cards_2nd_half"],
  // Soccer shots / shots on target. Each groups the two-sided O/U with its
  // one-sided N+ ladder, the same way the player props above do.
  "Total Shots": ["alternate_total_shots", "alternate_total_shots_milestones"],
  "Total Shots - 1st Half": ["alternate_total_shots_1st_half", "alternate_total_shots_milestones_1st_half"],
  "Total Shots - 2nd Half": ["alternate_total_shots_2nd_half", "alternate_total_shots_milestones_2nd_half"],
  "Total Shots on Target": ["alternate_total_shots_on_target", "alternate_total_shots_on_target_milestones"],
  "Total Shots on Target - 1st Half": ["alternate_total_shots_on_target_1st_half", "alternate_total_shots_on_target_milestones_1st_half"],
  "Total Shots on Target - 2nd Half": ["alternate_total_shots_on_target_2nd_half", "alternate_total_shots_on_target_milestones_2nd_half"],

  // Tennis (Mens Slams) totals
  "Total Games": ["alternate_total_games"],
  "Total Sets": ["alternate_total_sets"],
  "Total Aces": ["alternate_total_aces"],
  "Total Games - 1st Set": ["alternate_total_games_1st_set"],
  "Total Games - 2nd Set": ["alternate_total_games_2nd_set"],

  // Spread Markets
  "Match Handicap (Asian)": ["alternate_lines"],
  "Match Handicap - 1st 5 Innings (Asian)": ["alternate_lines_1st_5_innings"],
  "Match Handicap - 1st Half (Asian)": ["alternate_lines_1st_half"],
  "Match Handicap - 1st Period (Asian)": ["alternate_lines_1st_period"],
  "Match Handicap - 2nd Period (Asian)": ["alternate_lines_2nd_period"],
  "Match Handicap - 3rd Period (Asian)": ["alternate_lines_3rd_period"],
  // Soccer 3-way handicap — a DIFFERENT market from the 2-way Asian handicap
  // above, not a variant of it. Books offering both emit both.
  "Match Handicap - 3 Way": ["alternate_lines_3_way"],
  "Match Handicap - 3 Way 1st Half": ["alternate_lines_3_way_1st_half"],
  "Match Handicap - 3 Way 2nd Half": ["alternate_lines_3_way_2nd_half"],
  "Match Handicap - 2nd Half (Asian)": ["alternate_lines_2nd_half"],

  // Tennis (Mens Slams) handicaps — games/sets
  "Match Handicap - Games (Asian)": ["alternate_lines_games"],
  "Match Handicap - Sets (Asian)": ["alternate_lines_sets"],
  "Match Handicap - Games 1st Set (Asian)": ["alternate_lines_games_1st_set"],
  "Match Handicap - Games 2nd Set (Asian)": ["alternate_lines_games_2nd_set"],

  // Tennis (Mens Slams) correct score — set scoreline (Odds page only)
  "Correct Score (Sets)": ["correct_score_sets"],

  // Team Total Markets
  "Team Total Points": ["alternate_team_total_points"],
  "Team Total Tries": ["alternate_team_total_tries"],
  "Team Total Goals": ["alternate_team_total_goals"],
  "Team Total Corners": ["alternate_team_total_corners"],
  "Team Total Cards": ["alternate_team_total_cards"],
  "Team Total Runs": ["alternate_team_total_runs"],
  "Team Total Runs - 1st 5 Innings": ["alternate_team_total_runs_1st_5_innings"],
  "Team Total Runs - 1st Inning": ["alternate_team_total_runs_1st_1_innings"],
  "Team Total Points - 1st Half": ["alternate_team_total_points_1st_half"],
  "Team Total Tries - 1st Half": ["alternate_team_total_tries_1st_half"],
  "Team Total Goals - 1st Half": ["alternate_team_total_goals_1st_half"],
  "Team Total Corners - 1st Half": ["alternate_team_total_corners_1st_half"],
  "Team Total Cards - 1st Half": ["alternate_team_total_cards_1st_half"],
  "Team Total Goals - 2nd Half": ["alternate_team_total_goals_2nd_half"],
  "Team Total Corners - 2nd Half": ["alternate_team_total_corners_2nd_half"],
  "Team Total Cards - 2nd Half": ["alternate_team_total_cards_2nd_half"],
  "Team Total Shots": ["alternate_team_total_shots", "alternate_team_total_shots_milestones"],
  "Team Total Shots - 1st Half": ["alternate_team_total_shots_1st_half", "alternate_team_total_shots_milestones_1st_half"],
  "Team Total Shots - 2nd Half": ["alternate_team_total_shots_2nd_half", "alternate_team_total_shots_milestones_2nd_half"],
  "Team Total Shots on Target": ["alternate_team_total_shots_on_target", "alternate_team_total_shots_on_target_milestones"],
  "Team Total Shots on Target - 1st Half": ["alternate_team_total_shots_on_target_1st_half", "alternate_team_total_shots_on_target_milestones_1st_half"],
  "Team Total Shots on Target - 2nd Half": ["alternate_team_total_shots_on_target_2nd_half", "alternate_team_total_shots_on_target_milestones_2nd_half"],
  "Team Total Goals - 1st Period": ["alternate_team_total_goals_1st_period"],
  "Team Total Goals - 2nd Period": ["alternate_team_total_goals_2nd_period"],
  "Team Total Goals - 3rd Period": ["alternate_team_total_goals_3rd_period"],
}

export const MARKET_TYPE_HIERARCHY = {
  "Player Props": {
    "Basketball": [
      "Player Points",
      "Player Rebounds",
      "Player Assists",
      "Player Three Pointers Made",
      "Player Steals",
      "Player Blocks",
      "Points + Rebounds + Assists",
      "Points + Rebounds",
      "Points + Assists",
      "Rebounds + Assists",
    ],
    "Baseball": [
      "Batter Home Runs",
      "Batter Hits",
      "Batter RBIs",
      "Batter Runs",
      "Batter Singles",
      "Batter Total Bases",
      "Batter Walks",
      "Batter Strikeouts",
      "Batter Hits + Runs + RBIs",
      "Pitcher Strikeouts",
      "Pitcher Earned Runs",
      "Pitcher Hits Allowed",
      "Pitcher Walks Allowed",
      "Pitcher Outs Recorded",
    ],
    "American Football": [
      "Passing Yards",
      "Passing Touchdowns",
      "Longest Pass",
      "Passing Interceptions",
      "Passing + Rushing Yards",
      "Passing Completions",
      "Passing Attempts",
      "Rushing Yards",
      "Rushing Attempts",
      "Longest Rush",
      "Rushing + Receiving Yards",
      "Receiving Yards",
      "Receptions",
      "Longest Reception",
      "Tackles + Assists",
      "Sacks",
      "Kicking Points",
      "Field Goals Made",
      "Touchdown Scorer",
      "First Touchdown Scorer",
    ],
    "Hockey": [
      "Player Goals",
      "Player Points",
      "Player Shots on Goal",
      "Player Assists",
    ],
    "Aussie Rules": [
      "Player Disposals",
      "Player Fantasy Points",
      "Player Goals",
      "Player Marks",
      "Player Tackles",
      "Player Kicks",
      "Player Handballs",
      "Player Clearances",
    ],
    "Rugby League": [
      "Player Tries",
      "Player Points",
      "First Tryscorer",
      "First or Second Tryscorer",
      "First, Second or Third Tryscorer",
    ],
    "Soccer": [
      "First Goalscorer",
      "Player Goals",
      "Player Shots",
      "Player Shots on Target",
      "Player Assists",
      "Player Tackles (Soccer)",
      "Player Fouls Committed",
      "Player Cards",
      "Player Red Cards",
      "Goalkeeper Saves",
    ],
    "Tennis": [
      "Player Games Won",
      "Player Total Aces",
      "Player Sets Won",
    ],
  },
  "Game Markets": {
    "Main Markets": [
      "Head To Head",
      "Draw No Bet",
      "Both Teams To Score",
      "Fight Goes The Distance",
      "Head To Head - 3 Way",
      "Correct Score",
    ],
    "Period/Half Markets": [
      "Head To Head - 3 Way 1st Half",
      "Draw No Bet - 1st Half",
      "Both Teams To Score - 1st Half",
      "Head To Head - 1st 5 Innings",
      "Head To Head - 1st Half",
      "Head To Head - 1st Period",
      "Head To Head - 2nd Period",
      "Head To Head - 3rd Period",
      "Head To Head - 3 Way 2nd Half",
      "Draw No Bet - 2nd Half",
      "Both Teams To Score - 2nd Half",
      "Correct Score - 1st Half",
      "Correct Score - 2nd Half",
    ],
    // Tennis only, but the sport is not in the name: the dropdown already shows
    // markets for ONE selected league, so a sport name here only repeats what
    // the left column just said. Its siblings ("Main Markets") do not name a
    // sport either. NOT true of the "Tennis" sub-group under Player Props —
    // those keys are matched against SPORT_HIERARCHY group names and must stay.
    "Set Markets": [
      "Head To Head - 1st Set",
      "Head To Head - 2nd Set",
      "Correct Score (Sets)",
    ],
  },
  "Total Markets": {
    "Full Game Totals": [
      "Total Points",
      "Total Goals",
      "Total Tries",
      "Total Rounds",
      "Total Corners",
      "Total Cards",
      "Total Runs",
      "Total Touchdowns",
      "Total Shots",
      "Total Shots on Target",
    ],
    "Period/Half Totals": [
      "Total Runs - 1st 5 Innings",
      "Total Runs - 1st Inning",
      "Total Points - 1st Half",
      "Total Tries - 1st Half",
      "Total Goals - 1st Half",
      "Total Corners - 1st Half",
      "Total Cards - 1st Half",
      "Total Goals - 1st Period",
      "Total Goals - 2nd Period",
      "Total Goals - 3rd Period",
      "Total Goals - 2nd Half",
      "Total Corners - 2nd Half",
      "Total Cards - 2nd Half",
      "Total Shots - 1st Half",
      "Total Shots - 2nd Half",
      "Total Shots on Target - 1st Half",
      "Total Shots on Target - 2nd Half",
    ],
    // Tennis only; named for the markets rather than the sport, matching the
    // "Set Markets" note above and its "Full Game Totals" siblings.
    "Games, Sets & Aces": [
      "Total Games",
      "Total Sets",
      "Total Aces",
      "Total Games - 1st Set",
      "Total Games - 2nd Set",
    ],
  },
  "Spread Markets": {
    "Match Handicaps": [
      "Match Handicap (Asian)",
      "Match Handicap - 1st 5 Innings (Asian)",
      "Match Handicap - 1st Half (Asian)",
      "Match Handicap - 1st Period (Asian)",
      "Match Handicap - 2nd Period (Asian)",
      "Match Handicap - 3rd Period (Asian)",
      "Match Handicap - Games (Asian)",
      "Match Handicap - Sets (Asian)",
      "Match Handicap - Games 1st Set (Asian)",
      "Match Handicap - Games 2nd Set (Asian)",
      "Match Handicap - 2nd Half (Asian)",
      "Match Handicap - 3 Way",
      "Match Handicap - 3 Way 1st Half",
      "Match Handicap - 3 Way 2nd Half",
    ],
  },
  "Team Total Markets": {
    "Full Game Team Totals": [
      "Team Total Points",
      "Team Total Tries",
      "Team Total Goals",
      "Team Total Corners",
      "Team Total Cards",
      "Team Total Runs",
      "Team Total Shots",
      "Team Total Shots on Target",
    ],
    "Period/Half Team Totals": [
      "Team Total Runs - 1st 5 Innings",
      "Team Total Runs - 1st Inning",
      "Team Total Points - 1st Half",
      "Team Total Tries - 1st Half",
      "Team Total Goals - 1st Half",
      "Team Total Corners - 1st Half",
      "Team Total Cards - 1st Half",
      "Team Total Goals - 1st Period",
      "Team Total Goals - 2nd Period",
      "Team Total Goals - 3rd Period",
      "Team Total Goals - 2nd Half",
      "Team Total Corners - 2nd Half",
      "Team Total Cards - 2nd Half",
      "Team Total Shots - 1st Half",
      "Team Total Shots - 2nd Half",
      "Team Total Shots on Target - 1st Half",
      "Team Total Shots on Target - 2nd Half",
    ],
  },
}

export const MARKET_TYPE_MAPPING = {
  // Player Props - NBA/Basketball
  // Note: player_points and player_assists are defined in the NHL section below
  // (raw codes are shared between NBA and NHL).
  "player_rebounds": "Player Rebounds",
  "player_steals": "Player Steals",
  "player_blocks": "Player Blocks",
  "player_made_threes": "Player Three Pointers Made",
  "player_points_rebounds_assists": "Points + Rebounds + Assists",
  "player_points_rebounds": "Points + Rebounds",
  "player_points_assists": "Points + Assists",
  "player_rebounds_assists": "Rebounds + Assists",

  // NBA Player Milestone Props
  // Note: player_points_milestones and player_assists_milestones are in the NHL milestone section.
  "player_rebounds_milestones": "Player Rebounds Milestones",
  "player_steals_milestones": "Player Steals Milestones",
  "player_blocks_milestones": "Player Blocks Milestones",
  "player_made_threes_milestones": "Player Three Pointers Made Milestones",
  "player_points_rebounds_assists_milestones": "Points + Rebounds + Assists Milestones",
  "player_points_rebounds_milestones": "Points + Rebounds Milestones",
  "player_points_assists_milestones": "Points + Assists Milestones",
  "player_rebounds_assists_milestones": "Rebounds + Assists Milestones",

  // MLB Batter O/U Props
  "batter_home_runs": "Batter Home Runs",
  "batter_hits": "Batter Hits",
  "batter_rbis": "Batter RBIs",
  "batter_runs": "Batter Runs",
  "batter_singles": "Batter Singles",
  "batter_total_bases": "Batter Total Bases",
  "batter_walks": "Batter Walks",
  "batter_strikeouts": "Batter Strikeouts",
  "batter_hits_runs_rbis": "Batter Hits + Runs + RBIs",

  // MLB Batter Milestone Props
  "batter_home_runs_milestones": "Batter Home Run Milestones",
  "batter_hits_milestones": "Batter Hit Milestones",
  "batter_rbis_milestones": "Batter RBI Milestones",
  "batter_runs_milestones": "Batter Run Milestones",
  "batter_singles_milestones": "Batter Singles Milestones",
  "batter_total_bases_milestones": "Batter Total Bases Milestones",
  "batter_walks_milestones": "Batter Walks Milestones",
  "batter_strikeouts_milestones": "Batter Strikeout Milestones",
  "batter_hits_runs_rbis_milestones": "Batter Hits + Runs + RBI Milestones",

  // MLB Pitcher O/U Props
  "pitcher_strikeouts": "Pitcher Strikeouts",
  "pitcher_earned_runs": "Pitcher Earned Runs",
  "pitcher_hits_allowed": "Pitcher Hits Allowed",
  "pitcher_walks_allowed": "Pitcher Walks Allowed",
  "pitcher_outs": "Pitcher Outs Recorded",

  // MLB Pitcher Milestone Props
  "pitcher_strikeouts_milestones": "Pitcher Strikeout Milestones",
  "pitcher_earned_runs_milestones": "Pitcher Earned Runs Milestones",
  "pitcher_hits_allowed_milestones": "Pitcher Hits Allowed Milestones",
  "pitcher_walks_allowed_milestones": "Pitcher Walks Allowed Milestones",
  "pitcher_outs_milestones": "Pitcher Outs Recorded Milestones",

  // NFL Passing Props
  "player_passing_yds": "Passing Yards",
  "player_passing_tds": "Passing Touchdowns",
  "player_longest_pass": "Longest Pass",
  "player_passing_interceptions": "Passing Interceptions",
  "player_passing_rushing_yds": "Passing + Rushing Yards",
  "player_passing_completions": "Passing Completions",
  "player_passing_attempts": "Passing Attempts",

  // NFL Rushing Props
  "player_rushing_yds": "Rushing Yards",
  "player_rushing_attempts": "Rushing Attempts",
  "player_longest_rush": "Longest Rush",
  "player_rushing_receiving_yds": "Rushing + Receiving Yards",

  // NFL Receiving Props
  "player_receiving_yds": "Receiving Yards",
  "player_receptions": "Receptions",
  "player_longest_reception": "Longest Reception",

  // NFL Defensive / Kicking Props
  "player_tackles_assists": "Tackles + Assists",
  "player_sacks": "Sacks",
  "player_kicking_points": "Kicking Points",
  "player_field_goals_made": "Field Goals Made",

  // NFL Touchdown Scorers
  "player_1st_touchdown_scorer": "First Touchdown Scorer",

  // NFL Milestones. Distinct display names so a row shows which variant it is,
  // per the header note on MARKET_TYPE_MAPPING.
  "player_touchdowns_milestones": "Touchdown Scorer",
  "player_passing_yds_milestones": "Passing Yards Milestones",
  "player_passing_tds_milestones": "Passing Touchdowns Milestones",
  "player_longest_pass_milestones": "Longest Pass Milestones",
  "player_passing_interceptions_milestones": "Passing Interceptions Milestones",
  "player_passing_rushing_yds_milestones": "Passing + Rushing Yards Milestones",
  "player_passing_completions_milestones": "Passing Completions Milestones",
  "player_passing_attempts_milestones": "Passing Attempts Milestones",
  "player_rushing_yds_milestones": "Rushing Yards Milestones",
  "player_rushing_attempts_milestones": "Rushing Attempts Milestones",
  "player_longest_rush_milestones": "Longest Rush Milestones",
  "player_rushing_receiving_yds_milestones": "Rushing + Receiving Yards Milestones",
  "player_receiving_yds_milestones": "Receiving Yards Milestones",
  "player_receptions_milestones": "Receptions Milestones",
  "player_longest_reception_milestones": "Longest Reception Milestones",
  "player_tackles_assists_milestones": "Tackles + Assists Milestones",
  "player_sacks_milestones": "Sacks Milestones",
  "player_kicking_points_milestones": "Kicking Points Milestones",
  "player_field_goals_made_milestones": "Field Goals Made Milestones",

  // NHL Player Props (shared codes with NBA)
  "player_goals": "Player Goals",
  "player_points": "Player Points",
  "player_shots_on_goal": "Player Shots on Goal",
  "player_assists": "Player Assists",

  // AFL Player Props
  "player_disposals": "Player Disposals",

  // AFL Player Milestone Props
  "player_disposals_milestones": "Player Disposals Milestones",
  "player_fantasy_points_milestones": "Player Fantasy Points Milestones",
  "player_marks_milestones": "Player Marks Milestones",
  "player_tackles_milestones": "Player Tackles Milestones",
  "player_kicks_milestones": "Player Kicks Milestones",
  "player_handballs_milestones": "Player Handballs Milestones",
  "player_clearances_milestones": "Player Clearances Milestones",

  // NRL Player Props
  // Note: player_points_milestones is in the NHL milestone section ("Player Points Milestones").
  "player_tries_milestones": "Player Tries Milestones",
  "player_1st_tryscorer": "First Tryscorer",
  "player_1st_or_2nd_tryscorer": "First or Second Tryscorer",
  "player_1st_2nd_or_3rd_tryscorer": "First, Second or Third Tryscorer",

  // Soccer Player Props
  // player_goals / player_assists (+ their milestones) are already mapped in the
  // NHL sections — the raw codes are shared, so they are deliberately not repeated.
  "player_1st_goalscorer": "First Goalscorer",
  "player_shots": "Player Shots",
  "player_shots_on_target": "Player Shots on Target",
  "player_tackles": "Player Tackles",
  "player_fouls_committed": "Player Fouls Committed",
  "player_cards": "Player Cards",
  "player_red_cards": "Player Red Cards",
  "goalkeeper_saves": "Goalkeeper Saves",

  // Soccer Player Milestone Props
  "player_shots_milestones": "Player Shots Milestones",
  "player_shots_on_target_milestones": "Player Shots on Target Milestones",
  "player_fouls_committed_milestones": "Player Fouls Committed Milestones",
  "player_cards_milestones": "Player Cards Milestones",
  "player_red_cards_milestones": "Player Red Cards Milestones",
  "goalkeeper_saves_milestones": "Goalkeeper Saves Milestones",

  // Tennis (Mens Slams) Player Props
  "player_games_won": "Player Games Won",
  "player_total_aces": "Player Total Aces",
  "player_total_aces_milestones": "Player Total Aces Milestones",
  "player_sets_won": "Player Sets Won",

  // NHL Player Milestone Props
  "player_goals_milestones": "Player Goals Milestones",
  "player_points_milestones": "Player Points Milestones",
  "player_shots_on_goal_milestones": "Player Shots on Goal Milestones",
  "player_assists_milestones": "Player Assists Milestones",

  // Game Markets
  "head_to_head": "Head To Head",
  "draw_no_bet": "Draw No Bet",
  "both_teams_to_score": "Both Teams To Score",
  "fight_goes_distance": "Fight Goes The Distance",
  "head_to_head_3_way": "Head To Head - 3 Way",
  "head_to_head_3_way_1st_half": "Head To Head - 3 Way 1st Half",
  "draw_no_bet_1st_half": "Draw No Bet - 1st Half",
  "both_teams_to_score_1st_half": "Both Teams To Score - 1st Half",
  "head_to_head_1st_5_innings": "Head To Head - 1st 5 Innings",
  "head_to_head_1st_half": "Head To Head - 1st Half",
  "head_to_head_1st_period": "Head To Head - 1st Period",
  "head_to_head_2nd_period": "Head To Head - 2nd Period",
  "head_to_head_3rd_period": "Head To Head - 3rd Period",
  "head_to_head_1st_set": "Head To Head - 1st Set",
  "head_to_head_2nd_set": "Head To Head - 2nd Set",
  "correct_score_sets": "Correct Score (Sets)",
  "correct_score": "Correct Score",
  "correct_score_1st_half": "Correct Score - 1st Half",
  "correct_score_2nd_half": "Correct Score - 2nd Half",
  "head_to_head_3_way_2nd_half": "Head To Head - 3 Way 2nd Half",
  "draw_no_bet_2nd_half": "Draw No Bet - 2nd Half",
  "both_teams_to_score_2nd_half": "Both Teams To Score - 2nd Half",

  // Total Markets
  "alternate_total_points": "Total Points",
  "alternate_total_goals": "Total Goals",
  "alternate_total_tries": "Total Tries",
  "alternate_total_rounds": "Total Rounds",
  "alternate_total_corners": "Total Corners",
  "alternate_total_cards": "Total Cards",
  "alternate_total_runs": "Total Runs",
  "alternate_total_runs_1st_5_innings": "Total Runs - 1st 5 Innings",
  "alternate_total_runs_1st_1_innings": "Total Runs - 1st Inning",
  "winning_margin_39": "Winning Margin 1-39 / 40+",
  "winning_margin_24": "Winning Margin 1-24 / 25+",
  "winning_margin_12": "Winning Margin 1-12 / 13+",
  "method_of_victory": "Method of Victory",
  "round_betting": "Round Betting",
  "how_fight_ends": "How Fight Ends",
  "fight_to_start_round": "Fight To Start Round",
  "player_significant_strikes_milestones": "Fighter Significant Strikes",
  "player_strikes_milestones": "Fighter Total Strikes",
  "player_takedowns_milestones": "Fighter Takedowns",
  "alternate_total_touchdowns": "Total Touchdowns",
  "alternate_total_points_1st_half": "Total Points - 1st Half",
  "alternate_total_tries_1st_half": "Total Tries - 1st Half",
  "alternate_total_goals_1st_half": "Total Goals - 1st Half",
  "alternate_total_corners_1st_half": "Total Corners - 1st Half",
  "alternate_total_cards_1st_half": "Total Cards - 1st Half",
  "alternate_total_goals_1st_period": "Total Goals - 1st Period",
  "alternate_total_goals_2nd_period": "Total Goals - 2nd Period",
  "alternate_total_goals_3rd_period": "Total Goals - 3rd Period",
  "alternate_total_goals_2nd_half": "Total Goals - 2nd Half",
  "alternate_total_corners_2nd_half": "Total Corners - 2nd Half",
  "alternate_total_cards_2nd_half": "Total Cards - 2nd Half",
  "alternate_total_shots": "Total Shots",
  "alternate_total_shots_milestones": "Total Shots Milestones",
  "alternate_total_shots_1st_half": "Total Shots - 1st Half",
  "alternate_total_shots_milestones_1st_half": "Total Shots Milestones - 1st Half",
  "alternate_total_shots_2nd_half": "Total Shots - 2nd Half",
  "alternate_total_shots_milestones_2nd_half": "Total Shots Milestones - 2nd Half",
  "alternate_total_shots_on_target": "Total Shots on Target",
  "alternate_total_shots_on_target_milestones": "Total Shots on Target Milestones",
  "alternate_total_shots_on_target_1st_half": "Total Shots on Target - 1st Half",
  "alternate_total_shots_on_target_milestones_1st_half": "Total Shots on Target Milestones - 1st Half",
  "alternate_total_shots_on_target_2nd_half": "Total Shots on Target - 2nd Half",
  "alternate_total_shots_on_target_milestones_2nd_half": "Total Shots on Target Milestones - 2nd Half",
  "alternate_total_games": "Total Games",
  "alternate_total_sets": "Total Sets",
  "alternate_total_aces": "Total Aces",
  "alternate_total_games_1st_set": "Total Games - 1st Set",
  "alternate_total_games_2nd_set": "Total Games - 2nd Set",

  // Spread Markets
  "alternate_lines": "Match Handicap (Asian)",
  "alternate_lines_1st_5_innings": "Match Handicap - 1st 5 Innings (Asian)",
  "alternate_lines_1st_half": "Match Handicap - 1st Half (Asian)",
  "alternate_lines_1st_period": "Match Handicap - 1st Period (Asian)",
  "alternate_lines_2nd_period": "Match Handicap - 2nd Period (Asian)",
  "alternate_lines_3rd_period": "Match Handicap - 3rd Period (Asian)",
  "alternate_lines_2nd_half": "Match Handicap - 2nd Half (Asian)",
  "alternate_lines_3_way": "Match Handicap - 3 Way",
  "alternate_lines_3_way_1st_half": "Match Handicap - 3 Way 1st Half",
  "alternate_lines_3_way_2nd_half": "Match Handicap - 3 Way 2nd Half",
  "alternate_lines_games": "Match Handicap - Games (Asian)",
  "alternate_lines_sets": "Match Handicap - Sets (Asian)",
  "alternate_lines_games_1st_set": "Match Handicap - Games 1st Set (Asian)",
  "alternate_lines_games_2nd_set": "Match Handicap - Games 2nd Set (Asian)",

  // Team Total Markets
  "alternate_team_total_points": "Team Total Points",
  "alternate_team_total_tries": "Team Total Tries",
  "alternate_team_total_goals": "Team Total Goals",
  "alternate_team_total_corners": "Team Total Corners",
  "alternate_team_total_cards": "Team Total Cards",
  "alternate_team_total_runs": "Team Total Runs",
  "alternate_team_total_runs_1st_5_innings": "Team Total Runs - 1st 5 Innings",
  "alternate_team_total_runs_1st_1_innings": "Team Total Runs - 1st Inning",
  "alternate_team_total_points_1st_half": "Team Total Points - 1st Half",
  "alternate_team_total_tries_1st_half": "Team Total Tries - 1st Half",
  "alternate_team_total_goals_1st_half": "Team Total Goals - 1st Half",
  "alternate_team_total_corners_1st_half": "Team Total Corners - 1st Half",
  "alternate_team_total_cards_1st_half": "Team Total Cards - 1st Half",
  "alternate_team_total_goals_2nd_half": "Team Total Goals - 2nd Half",
  "alternate_team_total_corners_2nd_half": "Team Total Corners - 2nd Half",
  "alternate_team_total_cards_2nd_half": "Team Total Cards - 2nd Half",
  "alternate_team_total_shots": "Team Total Shots",
  "alternate_team_total_shots_milestones": "Team Total Shots Milestones",
  "alternate_team_total_shots_1st_half": "Team Total Shots - 1st Half",
  "alternate_team_total_shots_milestones_1st_half": "Team Total Shots Milestones - 1st Half",
  "alternate_team_total_shots_2nd_half": "Team Total Shots - 2nd Half",
  "alternate_team_total_shots_milestones_2nd_half": "Team Total Shots Milestones - 2nd Half",
  "alternate_team_total_shots_on_target": "Team Total Shots on Target",
  "alternate_team_total_shots_on_target_milestones": "Team Total Shots on Target Milestones",
  "alternate_team_total_shots_on_target_1st_half": "Team Total Shots on Target - 1st Half",
  "alternate_team_total_shots_on_target_milestones_1st_half": "Team Total Shots on Target Milestones - 1st Half",
  "alternate_team_total_shots_on_target_2nd_half": "Team Total Shots on Target - 2nd Half",
  "alternate_team_total_shots_on_target_milestones_2nd_half": "Team Total Shots on Target Milestones - 2nd Half",
  "alternate_team_total_goals_1st_period": "Team Total Goals - 1st Period",
  "alternate_team_total_goals_2nd_period": "Team Total Goals - 2nd Period",
  "alternate_team_total_goals_3rd_period": "Team Total Goals - 3rd Period",
}

export const SELECTION_SHORT_NAMES = {
  // Player Combo Props
  "player_points_rebounds_assists": "PRA",
  "player_points_rebounds": "PR",
  "player_points_assists": "PA",
  "player_rebounds_assists": "RA",

  // Individual Player Props (NBA codes; "player_points" and "player_assists" shared with NHL)
  "player_points": "Points",
  "player_rebounds": "Rebounds",
  "player_assists": "Assists",
  "player_steals": "Steals",
  "player_blocks": "Blocks",
  "player_made_threes": "Threes",

  // NFL Props
  "player_passing_yds": "Pass Yds",
  "player_passing_tds": "Pass TDs",
  "player_passing_completions": "Completions",
  "player_rushing_yds": "Rush Yds",
  "player_receiving_yds": "Rec Yds",
  "player_receptions": "Receptions",
  "player_tackles_assists": "Tackles + Ast",
  "player_sacks": "Sacks",
  "player_kicking_points": "Kicking Pts",
  "player_field_goals_made": "FGs Made",

  // NHL Props (player_points / player_assists already covered above)
  "player_goals": "Goals",
  "player_shots_on_goal": "Shots",

  // AFL Props
  "player_disposals": "Disposals",
  "player_disposals_milestones": "Disposals",
  "player_fantasy_points_milestones": "Fantasy Pts",
  "player_marks_milestones": "Marks",
  "player_tackles_milestones": "Tackles",
  "player_kicks_milestones": "Kicks",
  "player_handballs_milestones": "Handballs",
  "player_clearances_milestones": "Clearances",

  // Soccer Props (player_goals / player_assists share the NHL/NBA codes above)
  "player_1st_goalscorer": "1st Goalscorer",
  "player_shots": "Shots",
  "player_shots_milestones": "Shots",
  "player_shots_on_target": "SOT",
  "player_shots_on_target_milestones": "SOT",
  "player_tackles": "Tackles",
  "player_fouls_committed": "Fouls",
  "player_fouls_committed_milestones": "Fouls",
  "player_cards": "Cards",
  "player_cards_milestones": "Cards",
  "player_red_cards": "Red Cards",
  "player_red_cards_milestones": "Red Cards",
  "goalkeeper_saves": "Saves",
  "goalkeeper_saves_milestones": "Saves",

  // Game Markets (totals that appear inside selection text)
  "alternate_total_tries": "Tries",
  "alternate_total_rounds": "Rounds",
  "player_significant_strikes_milestones": "Sig. Strikes",
  "player_strikes_milestones": "Strikes",
  "player_takedowns_milestones": "Takedowns",
  "alternate_total_points": "Points",
  "alternate_total_goals": "Goals",
  "alternate_total_runs": "Runs",
  "alternate_total_corners": "Corners",
  "alternate_total_cards": "Cards",
  "alternate_total_touchdowns": "TDs",
  "alternate_total_shots": "Shots",
  "alternate_total_shots_milestones": "Shots",
  "alternate_total_shots_on_target": "SOT",
  "alternate_total_shots_on_target_milestones": "SOT",

  // Tennis (Mens Slams) props/totals (O/U, appear mid-sentence)
  "player_games_won": "Games",
  "player_total_aces": "Aces",
  "player_total_aces_milestones": "Aces",
  "alternate_total_games": "Games",
  "alternate_total_sets": "Sets",
  "alternate_total_aces": "Aces",
}

export const ALL_MARKET_TYPES = Object.keys(MARKET_TYPE_FILTER_MAPPING)
